import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@shared/api/client';
import { getScopeForm } from '@shared/api/scope-form';
import type {
  AdminTransactionRow,
  TransactionStatus,
  Uuid,
} from '@shared/types/api';
import { AdminChatThread } from './chat/chat-thread';
import { useMe } from '@/hooks/use-me';
import { useSectionPermission } from '@/hooks/use-admin-permissions';
import {
  useAdminTransaction,
  useAdminTxHistory,
  useAdminUsers,
  useAssignAssociate,
  useForceCancel,
  useForceComplete,
  useMarkInvoicePaidOffline,
  useRevokeAssignment,
} from '@/hooks/use-admin';
import { CreateInvoiceDialog } from './create-invoice-dialog';
import { UploadFinalDialog } from './upload-final-dialog';
import { AdminScopeFormReviewDialog } from './admin-scope-form-review-dialog';

// Local because the admin app has its own (lighter) UI primitives.
const STATUS_TONE: Record<TransactionStatus, string> = {
  SERVICE_REQUESTED: 'bg-indigo-50 text-indigo-700',
  CHAT_CREATED: 'bg-sky-50 text-sky-700',
  ASSOCIATE_ASSIGNMENT_PENDING: 'bg-amber-50 text-amber-800',
  ASSOCIATE_ASSIGNED: 'bg-blue-50 text-blue-800',
  ASSOCIATE_ACCEPTANCE_PENDING: 'bg-amber-50 text-amber-800',
  ASSOCIATE_ACCEPTED: 'bg-blue-50 text-blue-800',
  QUOTATION_REVIEW_PENDING: 'bg-amber-50 text-amber-800',
  CLIENT_CONFIRMATION_PENDING: 'bg-teal-50 text-teal-800',
  CLIENT_CONFIRMED: 'bg-teal-50 text-teal-800',
  PAYMENT_PENDING: 'bg-amber-50 text-amber-800',
  INITIATED: 'bg-gray-100 text-gray-700',
  INVOICE_CREATED: 'bg-amber-50 text-amber-800',
  PAID: 'bg-blue-50 text-blue-800',
  PAYMENT_RECEIVED: 'bg-blue-50 text-blue-800',
  ASSIGNED: 'bg-blue-50 text-blue-800',
  ASSIGNED_REVOKED: 'bg-gray-100 text-gray-700',
  IN_PROGRESS: 'bg-brand-50 text-brand-800',
  DRAFT_PREPARATION: 'bg-brand-50 text-brand-800',
  CLIENT_DRAFT_CONFIRMATION: 'bg-purple-50 text-purple-800',
  REVIEW: 'bg-purple-50 text-purple-800',
  REVISION_REQUESTED: 'bg-orange-50 text-orange-800',
  SENT_FOR_APPROVAL: 'bg-indigo-50 text-indigo-800',
  APPROVED: 'bg-brand-50 text-brand-800',
  FINAL_RESULT_RECEIVED: 'bg-teal-50 text-teal-800',
  DELIVERED: 'bg-emerald-50 text-emerald-800',
  FINAL_DOC_READY: 'bg-emerald-50 text-emerald-800',
  GRIEVANCE_RAISED: 'bg-rose-50 text-rose-800',
  CLIENT_CONFIRMED_FINAL: 'bg-emerald-50 text-emerald-800',
  RATING_PENDING: 'bg-amber-50 text-amber-800',
  SETTLEMENT_PENDING: 'bg-amber-50 text-amber-800',
  SETTLED: 'bg-emerald-50 text-emerald-800',
  COMPLETE: 'bg-gray-100 text-gray-700',
  CLOSED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-50 text-red-700',
};

const STATUS_LABEL: Record<TransactionStatus, string> = {
  SERVICE_REQUESTED: 'Service Requested',
  CHAT_CREATED: 'Chat Created',
  ASSOCIATE_ASSIGNMENT_PENDING: 'Assignment Pending',
  ASSOCIATE_ASSIGNED: 'Associate Assigned',
  ASSOCIATE_ACCEPTANCE_PENDING: 'Acceptance Pending',
  ASSOCIATE_ACCEPTED: 'Associate Accepted',
  QUOTATION_REVIEW_PENDING: 'Quotation Review Pending',
  CLIENT_CONFIRMATION_PENDING: 'Client Confirmation Pending',
  CLIENT_CONFIRMED: 'Associate Verified',
  PAYMENT_PENDING: 'Payment Pending',
  INITIATED: 'Chat Created',
  INVOICE_CREATED: 'Invoice Sent',
  PAID: 'Paid',
  PAYMENT_RECEIVED: 'Payment Received',
  ASSIGNED: 'Associate Assigned',
  ASSIGNED_REVOKED: 'Reassigning',
  IN_PROGRESS: 'In Progress',
  DRAFT_PREPARATION: 'Draft Preparation',
  CLIENT_DRAFT_CONFIRMATION: 'Draft Under Review',
  REVIEW: 'In Review',
  REVISION_REQUESTED: 'Revision Requested',
  SENT_FOR_APPROVAL: 'Sent For Approval',
  APPROVED: 'Approved',
  FINAL_RESULT_RECEIVED: 'Final Result Ready',
  DELIVERED: 'Delivered',
  FINAL_DOC_READY: 'Final Doc Ready',
  GRIEVANCE_RAISED: 'Grievance / Cancellation Pending',
  CLIENT_CONFIRMED_FINAL: 'Client Confirmed',
  RATING_PENDING: 'Rating Pending',
  SETTLEMENT_PENDING: 'Settlement Pending',
  SETTLED: 'Payout Settled',
  COMPLETE: 'Completed',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export function AdminTransactionDetailPage() {
  const { id } = useParams<{ id: Uuid }>();
  const navigate = useNavigate();
  const txPerm = useSectionPermission('TRANSACTIONS');
  const { data: tx, isLoading } = useAdminTransaction(id);
  const { data: history = [] } = useAdminTxHistory(id);
  const { data: me } = useMe();
  const [panel, setPanel] = useState<Panel | null>(null);
  const [mobileTab, setMobileTab] = useState<'chat' | 'details'>('chat');
  const qc = useQueryClient();

  const { data: scopeForm } = useQuery({
    queryKey: ['scope-form', id],
    queryFn: () => getScopeForm(id!),
    enabled: !!id,
  });

  if (!id) return null;

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="flex w-full flex-wrap items-center gap-3 px-6 py-3">
          <button
            onClick={() => navigate('/transactions')}
            className="text-xs text-gray-500 hover:underline"
          >
            ← Back to transactions
          </button>
          {isLoading || !tx ? (
            <span className="text-sm text-gray-500">Loading…</span>
          ) : (
            <>
              <h1 className="text-sm font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                <span>{tx.service.name}</span>
                <span className="font-mono text-xs text-gray-500">#{tx.code}</span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(tx.id);
                    toast.success('Transaction ID copied to clipboard');
                  }}
                  className="inline-flex items-center gap-1 rounded bg-gray-100 hover:bg-gray-200 px-1.5 py-0.5 text-[11px] font-mono text-gray-600 transition-colors cursor-pointer"
                  title={`Click to copy UUID: ${tx.id}`}
                >
                  <span>Copy ID</span>
                  <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </h1>
              <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide', STATUS_TONE[tx.status])}>
                {STATUS_LABEL[tx.status] ?? tx.status.replace(/_/g, ' ')}
              </span>
              {tx.narration ? (
                <div className="bg-brand-50 border border-brand-200/80 text-brand-900 px-2.5 py-1 rounded-md text-xs font-medium max-w-full sm:max-w-md flex items-center gap-1" title="Status narration">
                  <span className="font-semibold text-brand-800 shrink-0">Narration:</span>
                  <span className="break-words">{tx.narration}</span>
                </div>
              ) : null}
              <StateActionBar tx={tx} onPanel={setPanel} txPerm={txPerm} />
            </>
          )}
        </div>
      </header>

      {/* Mobile view tabs */}
      <div className="flex border-b border-gray-200 bg-white md:hidden shrink-0">
        <button
          onClick={() => setMobileTab('chat')}
          className={cn(
            'flex-1 py-3 text-center text-sm font-medium border-b-2 transition-colors',
            mobileTab === 'chat'
              ? 'border-brand-700 text-brand-800'
              : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
        >
          Chat
        </button>
        <button
          onClick={() => setMobileTab('details')}
          className={cn(
            'flex-1 py-3 text-center text-sm font-medium border-b-2 transition-colors',
            mobileTab === 'details'
              ? 'border-brand-700 text-brand-800'
              : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
        >
          Details
        </button>
      </div>

      <div className="grid w-full flex-1 grid-cols-1 gap-0 overflow-hidden md:grid-cols-[340px_1fr]">
        <aside
          className={cn(
            'flex-col overflow-y-auto border-r border-gray-200 bg-white p-5',
            mobileTab === 'details' ? 'flex' : 'hidden md:flex',
          )}
        >
          {tx ? (
            <>
              <Section title="Parties">
                <Field label="Client">
                  {tx.client
                    ? `${[tx.client.firstName, tx.client.lastName].filter(Boolean).join(' ') || tx.client.email}`
                    : '—'}
                  {tx.client ? (
                    <div className="text-xs text-gray-500">{tx.client.email}</div>
                  ) : null}
                </Field>
                <Field label="Associate">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {tx.assignedAssociate ? (
                        <>
                          <div className="truncate font-medium">
                            {[tx.assignedAssociate.firstName, tx.assignedAssociate.lastName]
                              .filter(Boolean)
                              .join(' ') || tx.assignedAssociate.email}
                          </div>
                          <div className="truncate text-xs text-gray-500">{tx.assignedAssociate.email}</div>
                          {tx.associateVerificationStatus ? (
                            <div className="mt-1">
                              <span className={cn(
                                "inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold border",
                                tx.associateVerificationStatus === 'VERIFIED_BY_CLIENT' ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                                tx.associateVerificationStatus === 'PENDING_CLIENT_VERIFICATION' ? "bg-amber-50 text-amber-800 border-amber-200 animate-pulse" :
                                "bg-red-50 text-red-800 border-red-200"
                              )}>
                                {tx.associateVerificationStatus === 'VERIFIED_BY_CLIENT' ? '✓ Verified by Client' :
                                 tx.associateVerificationStatus === 'PENDING_CLIENT_VERIFICATION' ? '⏳ Pending Client Verification' :
                                 '✕ Rejected by Client'}
                              </span>
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <div>
                          <span className="text-amber-700 font-medium">unassigned</span>
                          {(tx.associateRejectionCount ?? 0) > 0 ? (
                            <div className="mt-1 text-xs text-red-700 font-medium bg-red-50 p-1.5 rounded border border-red-200">
                              Client requested associate change ({(tx.associateRejectionCount ?? 0)}/2 changes used). Please reassign.
                            </div>
                          ) : (tx.status === 'INITIATED' || tx.status === 'INVOICE_CREATED') ? (
                            <span className="block text-[10px] text-gray-400 mt-0.5 leading-normal">
                              Requires payment before assignment
                            </span>
                          ) : null}
                        </div>
                      )}
                      {(tx.associateRejectionCount ?? 0) > 0 && (
                        <div className="mt-1 text-[11px] font-medium text-gray-600">
                          Rejections used: <span className="font-bold text-gray-900">{tx.associateRejectionCount}/2</span>
                        </div>
                      )}
                    </div>
                    {/* Management actions */}
                    {(txPerm.canEdit || txPerm.isSuperAdmin) && (
                      tx.status === 'ASSIGNED' ? (
                        <Button
                          variant="ghost"
                          onClick={() => setPanel('revoke')}
                          className="text-[11px] text-red-600 hover:bg-red-50 hover:text-red-700 px-2 py-0.5 h-6 shrink-0"
                        >
                          Revoke
                        </Button>
                      ) : (tx.status === 'PAID' || tx.status === 'ASSIGNED_REVOKED') ? (
                        <Button
                          variant="outline"
                          onClick={() => setPanel('assign')}
                          className="text-[11px] px-2 py-0.5 h-6 shrink-0"
                        >
                          Manage
                        </Button>
                      ) : null
                    )}
                  </div>
                </Field>
                {tx.candidateAssociates && tx.candidateAssociates.length > 0 ? (
                  <CandidateAssociatesList tx={tx} />
                ) : null}
              </Section>

              <Section title="Service">
                <Field label="Service">{tx.service.name}</Field>
                {tx.subService ? <Field label="Sub-service">{tx.subService.name}</Field> : null}
              </Section>

              <Section title="Timeline">
                <Field label="Initiated">{new Date(tx.initiatedAt).toLocaleString()}</Field>
                <Field label="Updated">{new Date(tx.updatedAt).toLocaleString()}</Field>
                {tx.cancellationReason ? (
                  <Field label="Cancel reason">{tx.cancellationReason}</Field>
                ) : null}
              </Section>

              <Section title="State history">
                <ol className="space-y-1.5 text-xs text-gray-700">
                  {history.length === 0 ? (
                    <li className="text-gray-500">No transitions yet.</li>
                  ) : history.map((h) => (
                    <li key={h.id} className="flex gap-2">
                      <span className="shrink-0 text-gray-400">
                        {new Date(h.createdAt).toLocaleString()}
                      </span>
                      <span>
                        {h.fromStatus ? `${h.fromStatus} → ` : ''}
                        <span className="font-medium">{h.toStatus}</span>
                        {h.actor ? <span className="text-gray-500"> by {h.actor.email}</span> : null}
                        {h.reason ? <span className="italic text-gray-600"> — {h.reason}</span> : null}
                      </span>
                    </li>
                  ))}
                </ol>
              </Section>
            </>
          ) : null}
        </aside>

        <div className={cn('flex flex-col flex-1 min-h-0', mobileTab === 'chat' ? 'flex' : 'hidden md:flex')}>
          <AdminChatThread
            transactionId={id}
            myUserId={me?.id}
            readOnly={!txPerm.canEdit && !txPerm.isSuperAdmin}
          />
        </div>
      </div>


      {panel === 'assign' && id ? (
        <Dialog open onClose={() => setPanel(null)} title="Assign associate" className="max-w-md">
          <AssignPanel
            txId={id}
            clientId={tx?.clientId}
            serviceId={tx?.serviceId ?? tx?.service.id}
            initialCandidateIds={
              tx?.candidateAssociates
                ? tx.candidateAssociates.filter((c) => c.status !== 'DECLINED' && c.status !== 'EXPIRED').map((c) => c.id)
                : tx?.associateAssignmentStatus === 'DECLINED'
                ? []
                : tx?.candidateAssociateIds ?? []
            }
            onDone={() => {
              setPanel(null);
              qc.invalidateQueries({ queryKey: ['admin', 'tx'] });
            }}
          />
        </Dialog>
      ) : null}
      {panel === 'revoke' && id ? (
        <Dialog open onClose={() => setPanel(null)} title="Revoke assignment" className="max-w-md">
          <ReasonPanel
            description="Transaction returns to ASSIGNED_REVOKED and can be reassigned."
            kind="revoke"
            txId={id}
            onDone={() => setPanel(null)}
          />
        </Dialog>
      ) : null}
      {panel === 'cancel' && id ? (
        <Dialog open onClose={() => setPanel(null)} title="Force-cancel" className="max-w-md">
          <ReasonPanel
            description="Admin override cancel. Shown to the parties as the reason."
            kind="cancel"
            txId={id}
            onDone={() => setPanel(null)}
          />
        </Dialog>
      ) : null}
      {panel === 'createInvoice' && id ? (
        <CreateInvoiceDialog
          transactionId={id}
          isStartsFrom={tx?.service.isStartsFrom}
          defaultAssociateFeeRupees={
            tx?.service.totalAssociateFeeRupees ??
            tx?.service.defaultAssociateFeeRupees ??
            tx?.service.defaultApplicationFeeRupees
          }
          defaultPlatformFeeRupees={
            tx?.service.totalPlatformFeeRupees ??
            tx?.service.defaultPlatformFeeRupees ??
            tx?.service.defaultServiceChargeRupees
          }
          defaultGovernmentFeeRupees={
            tx?.service.governmentFeeRupees !== undefined && tx?.service.governmentFeeRupees !== null
              ? String(tx.service.governmentFeeRupees)
              : (tx?.service as any)?.governmentFeeRupees !== undefined && (tx?.service as any)?.governmentFeeRupees !== null
                ? String((tx?.service as any).governmentFeeRupees)
                : ''
          }
          additionalCharges={
            (tx?.service as any)?.additionalCharges ??
            ((tx?.service as any)?.stageRefundPolicy as any)?.additionalCharges
          }
          onClose={() => {
            setPanel(null);
            // Refetch the tx so status flips to INVOICE_CREATED and the
            // action bar re-renders with the Mark-paid-offline affordance
            // (when applicable).
            qc.invalidateQueries({ queryKey: ['admin', 'tx', id] });
            qc.invalidateQueries({ queryKey: ['admin', 'invoices'] });
          }}
        />
      ) : null}
      {panel === 'payOffline' && id && tx ? (
        <MarkPaidOfflineInlineDialog
          invoiceId={(tx.invoices ?? [])[0]?.id}
          invoiceNumber={(tx.invoices ?? [])[0]?.invoiceNumber}
          onClose={() => {
            setPanel(null);
            qc.invalidateQueries({ queryKey: ['admin', 'tx', id] });
            qc.invalidateQueries({ queryKey: ['admin', 'invoices'] });
          }}
        />
      ) : null}
      {panel === 'uploadFinal' && id ? (
        <UploadFinalDialog
          open
          transactionId={id}
          availableKinds={['FINAL']}
          defaultKind="FINAL"
          onClose={() => {
            setPanel(null);
            qc.invalidateQueries({ queryKey: ['admin', 'tx', id] });
          }}
        />
      ) : null}
      {panel === 'uploadSupport' && id ? (
        <UploadFinalDialog
          open
          transactionId={id}
          availableKinds={['SUPPORTING', 'DRAFT', 'REFERENCE']}
          defaultKind="SUPPORTING"
          title="Upload document"
          onClose={() => {
            setPanel(null);
            qc.invalidateQueries({ queryKey: ['admin', 'tx', id] });
          }}
        />
      ) : null}
      {panel === 'reviewScope' && id && scopeForm ? (
        <AdminScopeFormReviewDialog
          open
          transactionId={id}
          scopeForm={scopeForm}
          onClose={() => {
            setPanel(null);
            qc.invalidateQueries({ queryKey: ['scope-form', id] });
            qc.invalidateQueries({ queryKey: ['admin', 'tx', id] });
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Small dialog that collects the bank-transfer / UPI reference + optional
 * notes, then calls POST /invoices/:id/mark-paid-offline. Creates an
 * OFFLINE Payment row and fires the MARK_PAID_OFFLINE state transition
 * server-side, taking the tx from INVOICE_CREATED → PAID.
 */
function MarkPaidOfflineInlineDialog({
  invoiceId,
  invoiceNumber,
  onClose,
}: {
  invoiceId: Uuid | undefined;
  invoiceNumber: string | undefined;
  onClose: () => void;
}) {
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const mark = useMarkInvoicePaidOffline();

  if (!invoiceId) return null;

  function submit() {
    if (reference.trim().length < 3) return;
    if (!invoiceId) return;
    mark.mutate(
      { id: invoiceId, reference: reference.trim(), notes: notes.trim() || undefined },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Mark paid offline — ${invoiceNumber ?? ''}`}
      className="max-w-md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={reference.trim().length < 3 || mark.isPending}>
            {mark.isPending ? 'Recording…' : 'Mark paid'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-gray-700">
          Record that payment has been received out-of-band. The transaction will move to
          <span className="font-semibold"> PAID</span>, same as the Razorpay webhook path.
        </p>
        <label className="block">
          <div className="mb-1 text-xs font-medium text-gray-700">Bank / UPI reference *</div>
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Bank txn id, UPI reference, cheque no."
          />
        </label>
        <label className="block">
          <div className="mb-1 text-xs font-medium text-gray-700">Notes (optional)</div>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Cleared on 18 Apr, phone payment"
          />
        </label>
        {mark.isError ? (
          <p className="text-sm text-red-600">{apiErrorMessage(mark.error)}</p>
        ) : null}
      </div>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-5">
      <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="text-sm text-gray-900">{children}</div>
    </div>
  );
}

// ------------------------------------------------------------------
// State-transition action bar. Only surfaces transitions that the state
// machine actually accepts from the current status — mirrors the rules in
// TransactionStateMachineService so the UI never offers a bad click.
// ------------------------------------------------------------------
/**
 * Action bar rules — mirrors `TransactionStateMachineService` exactly so the
 * UI never offers a click the server will reject:
 *
 *   INITIATED           → Create invoice       (CREATE_INVOICE transition)
 *   INVOICE_CREATED +   → Mark paid offline    (MARK_PAID_OFFLINE — only
 *   OFFLINE invoice                              when the latest invoice's
 *                                                paymentMode is OFFLINE; for
 *                                                ONLINE mode the client
 *                                                pays via Razorpay and the
 *                                                webhook closes the loop)
 *   PAID                → Assign associate     (ASSIGN)
 *   ASSIGNED_REVOKED    → Assign associate     (ASSIGN)
 *   ASSIGNED / IN_PROGRESS / REVIEW / REVISION_REQUESTED /
 *   SENT_FOR_APPROVAL / APPROVED               → Revoke (REVOKE)
 *   FINAL_DOC_READY     → Force complete       (FORCE_COMPLETE)
 *   Any non-terminal    → Force cancel         (FORCE_CANCEL)
 *
 * Invoice creation is deliberately NOT exposed on the Invoices list page —
 * it's a state transition on the transaction and belongs here so the gate
 * matches the state machine.
 */
type Panel =
  | 'assign'
  | 'revoke'
  | 'cancel'
  | 'createInvoice'
  | 'payOffline'
  | 'uploadFinal'
  | 'uploadSupport'
  | 'reviewScope';

function StateActionBar({
  tx,
  onPanel,
  txPerm,
}: {
  tx: AdminTransactionRow;
  onPanel: (p: Panel) => void;
  txPerm: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    isSuperAdmin: boolean;
  };
}) {
  if (tx.status === 'CANCELLED' || tx.status === 'COMPLETE') {
    return null;
  }

  if (!txPerm.canEdit && !txPerm.canCreate && !txPerm.canDelete && !txPerm.isSuperAdmin) {
    return (
      <div className="ml-auto">
        <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2.5 py-1 rounded-md">View Only</span>
      </div>
    );
  }

  const { data: scopeForm } = useQuery({
    queryKey: ['scope-form', tx.id],
    queryFn: () => getScopeForm(tx.id),
    enabled: !!tx.id,
  });

  const forceComplete = useForceComplete();
  // The latest invoice (invoices[] comes back sorted desc by createdAt from
  // AdminTransactionsService.getOne). Used to decide whether offline-paid
  // collection is available for this tx.
  const latestInvoice = (tx.invoices ?? [])[0];

  const isStartsFrom = tx.service?.isStartsFrom ?? false;
  const associateAccepted = tx.associateAssignmentStatus === 'ACCEPTED';
  const hasActiveInvoice = latestInvoice !== undefined && latestInvoice.status !== 'CANCELLED';

  // Show scope form button only when:
  //   - Associate has accepted the assignment
  //   - A scope form has been created & submitted by the currently assigned associate
  //   - Status is NOT yet ACCEPTED by client (once accepted, it is locked — no editing)
  const isScopeSubmittedByCurrentAssociate =
    !!scopeForm?.createdByAssociateId &&
    !!tx.assignedAssociate?.id &&
    scopeForm.createdByAssociateId === tx.assignedAssociate.id;

  const canReviewScope =
    (txPerm.canEdit || txPerm.isSuperAdmin) &&
    isStartsFrom &&
    associateAccepted &&
    scopeForm !== null &&
    scopeForm !== undefined &&
    isScopeSubmittedByCurrentAssociate &&
    scopeForm.status !== 'ACCEPTED';

  // For startsFrom services, invoice creation is strictly gated on associate accepting the assignment first.
  const canCreateInvoice = (txPerm.canCreate || txPerm.canEdit || txPerm.isSuperAdmin) && (isStartsFrom
    ? (associateAccepted && !hasActiveInvoice)
    : ((['INITIATED', 'SERVICE_REQUESTED', 'CHAT_CREATED', 'ASSOCIATE_ASSIGNED', 'ASSOCIATE_ACCEPTED', 'CLIENT_CONFIRMED', 'ASSIGNED', 'IN_PROGRESS', 'PAYMENT_PENDING'].includes(tx.status)) && !hasActiveInvoice));

  const canMarkPaidOffline =
    (txPerm.canEdit || txPerm.isSuperAdmin) &&
    (tx.status === 'INVOICE_CREATED' || tx.status === 'PAYMENT_PENDING') &&
    latestInvoice !== undefined &&
    latestInvoice.paymentMode === 'OFFLINE' &&
    (latestInvoice.status === 'DRAFT' || latestInvoice.status === 'SENT');

  const isMaxRejectionsReached = (tx.associateRejectionCount ?? 0) >= 2;

  // Action Bar Assignment Rules:
  // - For startsFrom services: Admin assigns associate first at INITIATED state (or when unassigned/awaiting acceptance).
  // - For normal services: Admin assigns associate ONLY AFTER payment is completed (status === 'PAID' or ASSIGNED_REVOKED).
  const canAssign = (txPerm.canEdit || txPerm.isSuperAdmin) && !isMaxRejectionsReached && (isStartsFrom
    ? (['INITIATED', 'SERVICE_REQUESTED', 'CHAT_CREATED', 'ASSOCIATE_ASSIGNMENT_PENDING', 'ASSIGNED_REVOKED'].includes(tx.status) || !tx.assignedAssociate || !associateAccepted)
    : (['PAID', 'PAYMENT_RECEIVED', 'ASSOCIATE_ASSIGNMENT_PENDING', 'ASSIGNED_REVOKED'].includes(tx.status) || (!['INITIATED', 'SERVICE_REQUESTED', 'CHAT_CREATED', 'INVOICE_CREATED', 'PAYMENT_PENDING'].includes(tx.status) && !tx.assignedAssociate)));

  // Revoke is legal once an associate is assigned and has accepted.
  const canRevoke = (txPerm.canEdit || txPerm.isSuperAdmin) && (tx.status === 'ASSIGNED' || tx.status === 'ASSOCIATE_ASSIGNED' || tx.status === 'ASSOCIATE_ACCEPTED') && tx.assignedAssociate !== null && associateAccepted;
  // Admin-side escape hatch: upload the final document on the associate's
  // behalf once the client has APPROVED. Server gates this to FINAL-only at
  // APPROVED for every role (see DocumentsService.assertCanUpload).
  const canUploadFinal = (txPerm.canEdit || txPerm.isSuperAdmin) && ['APPROVED', 'CLIENT_CONFIRMED_FINAL', 'FINAL_RESULT_RECEIVED'].includes(tx.status);
  // FINAL_DOC_READY is the support window — admin can push any non-FINAL
  // document (addenda, corrections, references) while the tx stays open.
  const canUploadSupport = (txPerm.canEdit || txPerm.isSuperAdmin) && ['FINAL_DOC_READY', 'DELIVERED'].includes(tx.status);
  const canForceComplete = (txPerm.canEdit || txPerm.isSuperAdmin) && ['FINAL_DOC_READY', 'DELIVERED', 'CLIENT_CONFIRMED_FINAL', 'RATING_PENDING', 'SETTLEMENT_PENDING'].includes(tx.status);
  // Mirror server CANCEL.from: cancellation stops being legal once the
  // client has APPROVED — the tx is committed to offline proceedings.
  const canForceCancel = (txPerm.canDelete || txPerm.isSuperAdmin) && !['APPROVED', 'CLIENT_CONFIRMED_FINAL', 'FINAL_DOC_READY', 'COMPLETE', 'CLOSED'].includes(tx.status);

  const anyPrimary =
    canReviewScope ||
    canCreateInvoice ||
    canMarkPaidOffline ||
    canAssign ||
    canUploadFinal ||
    canUploadSupport ||
    canForceComplete;

  return (
    <div className="ml-auto flex flex-wrap items-center gap-1.5">
      {canReviewScope ? (
        <ActionButton onClick={() => onPanel('reviewScope')}>
          {scopeForm?.status === 'PROPOSED' ? 'Review Scope Form' : 'View / Edit Scope Form'}
        </ActionButton>
      ) : null}
      {canCreateInvoice ? (
        <ActionButton onClick={() => onPanel('createInvoice')}>Create invoice</ActionButton>
      ) : null}
      {canMarkPaidOffline ? (
        <ActionButton onClick={() => onPanel('payOffline')}>Mark paid offline</ActionButton>
      ) : null}
      {canAssign ? (
        <ActionButton onClick={() => onPanel('assign')}>Assign associate</ActionButton>
      ) : null}
      {canRevoke ? (
        <ActionButton variant="outline" onClick={() => onPanel('revoke')}>
          Revoke
        </ActionButton>
      ) : null}
      {canUploadFinal ? (
        <ActionButton onClick={() => onPanel('uploadFinal')}>Upload final document</ActionButton>
      ) : null}
      {canUploadSupport ? (
        <ActionButton variant="outline" onClick={() => onPanel('uploadSupport')}>
          Upload document
        </ActionButton>
      ) : null}
      {canForceComplete ? (
        <ActionButton
          onClick={() => forceComplete.mutate(tx.id)}
          disabled={forceComplete.isPending}
        >
          {forceComplete.isPending ? 'Completing…' : 'Force complete'}
        </ActionButton>
      ) : null}
      {canForceCancel ? (
        <ActionButton variant="outline" onClick={() => onPanel('cancel')}>
          Force cancel
        </ActionButton>
      ) : null}
      {!anyPrimary && !canRevoke && !canForceCancel ? (
        <span className="text-[11px] text-gray-500">No admin transitions available.</span>
      ) : null}
    </div>
  );
}
function ActionButton({
  variant = 'primary',
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'outline' }) {
  return (
    <button
      {...rest}
      className={cn(
        'inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-xs font-medium leading-none transition-colors disabled:opacity-50 shadow-sm',
        variant === 'primary'
          ? 'border-transparent bg-brand-700 text-white hover:bg-brand-800'
          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
        className,
      )}
    />
  );
}

// ------------------------------------------------------------------
// Chat thread implementation moved to ./chat/chat-thread.tsx to match the
// client chat's visual design (bubbles with tails, avatars, day separators,
// hover cards on associate/client senders). See `AdminChatThread`.
// ------------------------------------------------------------------

export const ASSIGNMENT_OFFER_DURATION_MS = 2 * 60 * 60 * 1000; // 2 hours

function CandidateAssociatesList({ tx }: { tx: any }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (tx.associateAssignmentStatus === 'ACCEPTED') return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [tx.associateAssignmentStatus]);

  return (
    <Field
      label={
        <div className="flex items-center justify-between w-full">
          <span>Assigned Candidate Associates</span>
        </div>
      }
    >
      <div className="space-y-1.5 mt-1">
        {tx.candidateAssociates?.map((c: any) => {
          const assocAssignedAt = c.assignedAt ?? tx.candidateAssignedAt?.[c.id] ?? tx.assignedAt;
          const assignedAtTime = assocAssignedAt ? new Date(assocAssignedAt).getTime() : 0;
          const expiresAtTime = assignedAtTime > 0 ? assignedAtTime + ASSIGNMENT_OFFER_DURATION_MS : 0;
          const remainingSecs = expiresAtTime > 0 ? Math.max(0, Math.ceil((expiresAtTime - now) / 1000)) : 0;
          const isItemExpired = c.status === 'EXPIRED' || (c.status === 'PENDING' && expiresAtTime > 0 && remainingSecs <= 0);

          const status =
            (isItemExpired || (tx.assignedAssociateId && c.id !== tx.assignedAssociateId && c.status !== 'DECLINED' && c.status !== 'ACCEPTED')) &&
            c.status === 'PENDING'
              ? 'EXPIRED'
              : c.status;

          const hours = Math.floor(remainingSecs / 3600);
          const mins = Math.floor((remainingSecs % 3600) / 60);
          const secs = remainingSecs % 60;
          const timerText = hours > 0
            ? `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
            : `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

          return (
            <div
              key={c.id}
              className="flex items-center justify-between text-xs rounded-md border border-gray-200 bg-gray-50/70 p-2"
            >
              <div className="min-w-0 flex-1 pr-2">
                <div className="truncate font-medium text-gray-900">
                  {[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email}
                </div>
                {c.email ? <div className="truncate text-[11px] text-gray-500">{c.email}</div> : null}
              </div>
              <div className="shrink-0 flex items-center gap-1.5">
                {status === 'PENDING' && !isItemExpired ? (
                  <span className="text-[10px] font-mono font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-ping" />
                    {timerText}
                  </span>
                ) : null}
                {status === 'ACCEPTED' ? (
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 border border-emerald-200">
                    Accepted
                  </span>
                ) : status === 'DECLINED' ? (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800 border border-red-200">
                    Declined
                  </span>
                ) : status === 'EXPIRED' ? (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 border border-amber-200">
                    Expired
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 border border-amber-200">
                    Pending
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Field>
  );
}

// ------------------------------------------------------------------
// Assign / Reason panels — lifted from the original TxDetailDialog.
// ------------------------------------------------------------------
function AssignPanel({
  txId,
  clientId,
  serviceId,
  initialCandidateIds = [],
  onDone,
}: {
  txId: Uuid;
  clientId?: string;
  serviceId?: string;
  initialCandidateIds?: string[];
  onDone: () => void;
}) {
  const [q, setQ] = useState('');
  const [reason, setReason] = useState('');
  const [chosenIds, setChosenIds] = useState<string[]>(initialCandidateIds);

  const { data } = useAdminUsers({
    role: 'ASSOCIATE',
    serviceId,
    q: q.trim() || undefined,
    pageSize: 20,
  });
  const { data: franchise } = useAdminUsers({
    role: 'FRANCHISE',
    serviceId,
    q: q.trim() || undefined,
    pageSize: 20,
  });
  const assign = useAssignAssociate();

  const candidates = [...(data?.items ?? []), ...(franchise?.items ?? [])]
    .filter((u) => u.id !== clientId)
    .slice(0, 30);

  const toggleSelect = (id: string) => {
    setChosenIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      if (prev.length >= 5) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const isLimitReached = chosenIds.length >= 5;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-gray-700">
          Select 1 to 5 KYC-VERIFIED Associates to receive assignment request.
        </p>
        <span className="shrink-0 text-xs font-semibold px-2 py-0.5 rounded bg-brand-50 text-brand-700 border border-brand-200">
          Selected ({chosenIds.length}/5)
        </span>
      </div>

      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name / email / mobile"
      />

      {isLimitReached && (
        <p className="text-xs text-amber-700 font-medium">
          Maximum 5 associates selected. Uncheck a candidate to select a different associate.
        </p>
      )}

      <ul className="max-h-52 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
        {candidates.length === 0 ? (
          <li className="p-3 text-sm text-gray-500">No candidate associates found.</li>
        ) : (
          candidates.map((u) => {
            const isSelected = chosenIds.includes(u.id);
            const isDisabled = !isSelected && isLimitReached;

            return (
              <li key={u.id}>
                <label
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-2 text-left text-sm cursor-pointer select-none transition-colors',
                    isSelected && 'bg-brand-50/70',
                    isDisabled && 'opacity-50 cursor-not-allowed bg-gray-50',
                    !isSelected && !isDisabled && 'hover:bg-gray-50',
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => toggleSelect(u.id)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                    />
                    <div className="min-w-0 flex-1">
                      <span className="block font-medium text-gray-900 truncate">
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
                      </span>
                      <span className="block text-xs text-gray-500 truncate">
                        {u.email}
                        {u.status !== 'ACTIVE' ? ` · ${u.status}` : ''}
                      </span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 ml-2 shrink-0">{u.role}</span>
                </label>
              </li>
            );
          })
        )}
      </ul>

      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason for assignment (optional)"
      />

      {assign.isError ? (
        <p className="text-sm text-red-600">{apiErrorMessage(assign.error)}</p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          disabled={chosenIds.length === 0 || assign.isPending}
          onClick={() =>
            chosenIds.length > 0 &&
            assign.mutate(
              { id: txId, associateUserIds: chosenIds, reason: reason.trim() || undefined },
              { onSuccess: onDone },
            )
          }
        >
          {assign.isPending ? 'Assigning…' : `Assign (${chosenIds.length})`}
        </Button>
      </div>
    </div>
  );
}

function ReasonPanel({
  description,
  kind,
  txId,
  onDone,
}: {
  description: string;
  kind: 'revoke' | 'cancel';
  txId: Uuid;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const revoke = useRevokeAssignment();
  const cancel = useForceCancel();
  const minLen = kind === 'revoke' ? 3 : 5;
  const m = kind === 'revoke' ? revoke : cancel;

  function submit() {
    if (reason.trim().length < minLen) return;
    if (kind === 'revoke')
      revoke.mutate({ id: txId, reason: reason.trim() }, { onSuccess: onDone });
    else cancel.mutate({ id: txId, reason: reason.trim() }, { onSuccess: onDone });
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">{description}</p>
      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={`Reason (min ${minLen} chars)`}
      />
      {m.isError ? <p className="text-sm text-red-600">{apiErrorMessage(m.error)}</p> : null}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Back
        </Button>
        <Button disabled={reason.trim().length < minLen || m.isPending} onClick={submit}>
          {m.isPending ? 'Working…' : 'Confirm'}
        </Button>
      </div>
    </div>
  );
}

export { AdminTransactionDetailPage as default };
// Used by the list page's row click handler.
export function AdminTxRowLink({ id, children }: { id: Uuid; children: React.ReactNode }) {
  return (
    <Link to={`/transactions/${id}`} className="text-brand-700 hover:underline">
      {children}
    </Link>
  );
}
